/**
 * Kanal fiyat import modal'i. Kanal (parser_type) + dosya -> parse -> onizleme ->
 * POST /channel-prices/bulk. Asamali eslestirme sonuclari (byLayer) gosterilir.
 */

import React, { useState, useRef } from 'react';
import { X, Upload, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { useModalA11y } from '../../hooks/useModalA11y';
import { apiBulkSaveChannelPrices, type ChannelInfo, type ChannelBulkRow, type ChannelBulkResult } from '../../services/api';
import { parseByType } from './channelParsers';
import { logger } from '../../utils/logger';

interface Props {
  isOpen: boolean;
  channel: ChannelInfo | null;
  country: string;
  onClose: () => void;
  onComplete: () => void;
}

const ChannelImportModal: React.FC<Props> = ({ isOpen, channel, country, onClose, onComplete }) => {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ChannelBulkRow[] | null>(null);
  const [parseInfo, setParseInfo] = useState<{ totalLines: number; skipped: number; fileName: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ChannelBulkResult | null>(null);

  const handleCloseRef = useRef<() => void>(() => onClose());
  const modalRef = useModalA11y(isOpen, () => handleCloseRef.current());

  if (!isOpen || !channel) return null;

  const reset = () => { setParsed(null); setParseInfo(null); setError(null); setResult(null); };
  const handleClose = () => { reset(); onClose(); };
  handleCloseRef.current = handleClose;

  const handleFile = async (file: File) => {
    setError(null);
    setResult(null);
    try {
      const text = await file.text();
      const outcome = parseByType(channel.parser_type, text);
      if (outcome.rows.length === 0) {
        setError(tr ? 'Dosyada eslestirilebilir satir bulunamadi.' : 'No usable rows found.');
        setParsed(null);
        return;
      }
      setParsed(outcome.rows);
      setParseInfo({ totalLines: outcome.totalLines, skipped: outcome.skipped, fileName: file.name });
    } catch (e) {
      logger.error('Channel parse error:', e);
      setError(e instanceof Error ? e.message : String(e));
      setParsed(null);
    }
  };

  const handleImport = async () => {
    if (!parsed || !channel) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiBulkSaveChannelPrices(channel.code, country, parsed);
      setResult(res);
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div ref={modalRef} className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              {tr ? 'Fiyat İçe Aktar' : 'Import Prices'} — {channel.store_label}
            </h3>
            <p className="text-xs text-slate-500">{country} · {channel.parser_type}</p>
          </div>
          <button onClick={handleClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Result */}
          {result ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">{tr ? 'İçe aktarma tamamlandı' : 'Import complete'}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat label={tr ? 'Yeni' : 'Inserted'} value={result.inserted} />
                <Stat label={tr ? 'Güncellenen' : 'Updated'} value={result.updated} />
                <Stat label={tr ? 'Eşleşen' : 'Matched'} value={result.matched} good />
                <Stat label={tr ? 'Eşleşmeyen' : 'Unmatched'} value={result.unmatched} warn={result.unmatched > 0} />
              </div>
              {!!result.duplicatesRemoved && result.duplicatesRemoved > 0 && (
                <div className="text-xs text-slate-500">
                  {tr ? 'Tekrar eden SKU birleştirildi' : 'Duplicate SKUs merged'}: {result.duplicatesRemoved}
                </div>
              )}
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                <div className="font-medium mb-1">{tr ? 'Eşleştirme katmanları' : 'Match layers'}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>L1 (direkt): <b>{result.byLayer.L1_direct ?? 0}</b></span>
                  <span>L2 (sku_master): <b>{result.byLayer.L2_sku_master ?? 0}</b></span>
                  <span>L3 (manuel): <b>{result.byLayer.L3_mapping ?? 0}</b></span>
                  <span>L4 (başlık): <b>{result.byLayer.L4_title ?? 0}</b></span>
                  <span>{tr ? 'Eşleşmeyen' : 'Unmatched'}: <b>{result.byLayer.UNMATCHED ?? 0}</b></span>
                </div>
              </div>
              <button onClick={handleClose} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium">
                {tr ? 'Kapat' : 'Close'}
              </button>
            </div>
          ) : (
            <>
              {/* Dosya secimi */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors"
              >
                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">
                  {parseInfo ? parseInfo.fileName : (tr ? 'CSV dosyası seç' : 'Select CSV file')}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {channel.parser_type === 'walmart_item_report'
                    ? (tr ? 'Walmart Item Report (; ayraçlı)' : 'Walmart Item Report (; delimited)')
                    : (tr ? 'Shopify ürün export (.csv)' : 'Shopify product export (.csv)')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,text/csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Onizleme */}
              {parsed && parseInfo && (
                <div className="space-y-3">
                  <div className="text-sm text-slate-600">
                    {tr ? 'Bulunan satır' : 'Rows found'}: <b>{parsed.length}</b>
                    {parseInfo.skipped > 0 && (
                      <span className="text-slate-400"> · {tr ? 'atlanan (SKU yok)' : 'skipped (no SKU)'}: {parseInfo.skipped}</span>
                    )}
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2">SKU</th>
                          <th className="text-right px-3 py-2">{tr ? 'Fiyat' : 'Price'}</th>
                          <th className="text-right px-3 py-2">{tr ? 'Karşılaştırma' : 'Compare'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.slice(0, 8).map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-mono">{r.marketplace_sku}</td>
                            <td className="px-3 py-1.5 text-right">{r.price ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right text-slate-400">{r.compare_at ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={handleImport}
                    disabled={isLoading}
                    className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {tr ? `${parsed.length} satırı içe aktar` : `Import ${parsed.length} rows`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; good?: boolean; warn?: boolean }> = ({ label, value, good, warn }) => (
  <div className={`rounded-lg p-3 ${good ? 'bg-green-50' : warn ? 'bg-amber-50' : 'bg-slate-50'}`}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-xl font-bold ${good ? 'text-green-700' : warn ? 'text-amber-700' : 'text-slate-800'}`}>{value}</div>
  </div>
);

export default ChannelImportModal;
